import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FlexContainer, PureSpinner } from 'heliocor-ui'
import isEmpty from 'lodash/isEmpty'

import { 
    REQUEST_DELETE_OWNER, 
    ACTIVATE_CUSTOM_NAVIGATION, 
    DELETE_LOOKUP_SUGGESTION, 
    REQUEST_DATAS_FROM_OWNER_DOCS
} from '../store/cafe/actionTypes'


import { StructureTables } from '../components/structures/StructureTables'
import { ManageEntity } from '../components/structures/ManageEntity'
import { AddItem } from '../common'


const Structure = ({ updateNavigationButtons, wizardContainerRef }) => {
    
    const [view, setView] = useState()
    const [selectedEntity, setSelectedEntity] = useState()
    const [typeOfEntity, setTypeOfEntity] = useState()
    const [nextView, setNextView] = useState()
    const data = useSelector(state => state.party.connectedParties)
    const isRequest = useSelector(state => state.config.isRequest)
    const minSuggestedDirector = useSelector(state => state.settings.minSuggestedDirector)
    const minShareholderPercent = useSelector(state => state.settings.minShareholderPercent)
    const lookupStructureData = useSelector(state => state.party.lookupStructureData)
    const partiId = useSelector(state => state.party.data.identifier)
    const hasStructure = useSelector(state => state.party.connectedParties.length)

    const structureContainerRef = useRef()

    const dispatch = useDispatch()

    const getDatasFromOwnerDocs = useCallback(
        (owner) => dispatch({type: REQUEST_DATAS_FROM_OWNER_DOCS, owner}),
        [dispatch]
    )

    const handleDelete = (entity) => {
        dispatch({type: REQUEST_DELETE_OWNER, owner: entity})
    }

    const handleDeleteLookupSuggestion = (entity) => {
        dispatch({type: DELETE_LOOKUP_SUGGESTION, owner: entity})
    }
    
    const handleEdit = (entity, index, lookup) => {
        const entityType = entity.type
        setTypeOfEntity(entityType)
        setSelectedEntity({entity, index})
        lookup ? setNextView('EDIT_LOOKUP_ENTITY') : setNextView('EDIT_ENTITY')
        isRequest && getDatasFromOwnerDocs(entity)
    }

    const handleCreate = () => {
        setSelectedEntity()
        setNextView('CREATE_ENTITY')
    }

    const getStructureTableCaption = () => {
        switch (true) {
            case minSuggestedDirector === 1 && !!minShareholderPercent:
                return 'warning.partySuggestionsDirector'
            case minSuggestedDirector > 1 && !!minShareholderPercent:
                return 'warning.partySuggestionsMoreDirectors'
            case minSuggestedDirector === 1 && !(!!minShareholderPercent):
                return 'warning.partySuggestionsDirectorNoShares'
            case minSuggestedDirector > 1 && !(!!minShareholderPercent):
                return 'warning.partySuggestionsMoreDirectorsNoShares'  
            case !minSuggestedDirector && !!minShareholderPercent:
                return 'warning.partySuggestionsJustShare'
            case !hasStructure:
                return 'warning.pleaseAddParties'
            default:
                return ''
        }
    }

    
    const viewTypes = {
        STRUCTURE: { 
            id: 'STRUCTURE', 
            component: StructureTables, 
            props: {
                mainTitle: {
                    title: 'companyStructure',
                    subtitle: {
                        id: getStructureTableCaption(),
                        data:{director: minSuggestedDirector, percent: minShareholderPercent},
                        css: 'margin: .5rem 0 0 0 !important;'
                    },
                    translateSuggestedStructure: !isEmpty(lookupStructureData) ? `warning.suggestionForsuggestedStructure`: ''
                },
                data,
                handleEdit, 
                handleDelete, 
                handleDeleteLookupSuggestion,
                handler: handleCreate,
                extraContent: {
                    component: <AddItem onClick={handleCreate} translate='addEntity'/>
                }
            }},

        EDIT_ENTITY: { 
            id: 'EDIT_ENTITY', 
            component: ManageEntity,
            props: {
                isEdit: true, 
                typeOfEntity,
                selectedEntity,
                setNextView,
                wizardContainerRef,
                updateNavigationButtons,
                ownerCanBeEdited: selectedEntity?.entity?.relationStatus !== 'TEMPORAL'
            }
        },
        EDIT_LOOKUP_ENTITY: { 
            id: 'EDIT_LOOKUP_ENTITY', 
            component: ManageEntity,
            props: {
                isLookup: true,
                isEdit: true, 
                typeOfEntity,
                selectedEntity,
                setTypeOfEntity,
                setNextView,
                updateNavigationButtons,
                wizardContainerRef: wizardContainerRef
            }
        },
        CREATE_ENTITY: { 
            id: 'CREATE_ENTITY', 
            component: ManageEntity, 
            props: {
                selectedEntity,
                setNextView,
                updateNavigationButtons,
                wizardContainerRef: wizardContainerRef,
                defaultValues: {
                    parentId: partiId,
                    authorizedSignatory: false,
                    director: false,
                    shareholder: false,
                    ubo: false,
                    ownership: 0
                }
            }}
    }

    useEffect(() => { 
        if(!view) setView(viewTypes.STRUCTURE) 
    }, [viewTypes, selectedEntity, view, setView]) 

    useEffect(() => {
        if (structureContainerRef.current) structureContainerRef.current.scrollIntoView({ behavior: 'smooth' })
    }, [view])

    useEffect(() => { 
        const chooseView = () => {
            switch(nextView){
                case 'EDIT_ENTITY':
                    activateCustomNavigation(true)
                    setView(viewTypes.EDIT_ENTITY)
                    break
                case 'EDIT_LOOKUP_ENTITY':
                    activateCustomNavigation(true)
                    setView(viewTypes.EDIT_LOOKUP_ENTITY)
                    break
                case 'CREATE_ENTITY':
                    activateCustomNavigation(true)
                    setView(viewTypes.CREATE_ENTITY)
                    break
                case 'STRUCTURE':
                default:
                    activateCustomNavigation(false)
                    setView(viewTypes.STRUCTURE)
            }
        } 

        const activateCustomNavigation = (enabled) => dispatch({type: ACTIVATE_CUSTOM_NAVIGATION, enabled})

        if((nextView && nextView !== view.id)) chooseView()

    }, [selectedEntity, nextView, view, viewTypes, dispatch, data])


    useEffect(() => {
        setView(viewTypes.STRUCTURE)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[data])

    return(
        !data || !view ? <PureSpinner /> :
        <FlexContainer width='100%' column ref={structureContainerRef}>
            {React.createElement(view.component, {...view.props, typeOfEntity})}
        </FlexContainer>
    )
}

export { Structure }